import { Button } from "components/DesignSystem/Button/Button";
import Tab from "components/DesignSystem/Tab/Tab";
import * as Tabs from "@radix-ui/react-tabs";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { Cross } from "components/icons/Cross";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useModal } from "hooks/useModal";
import { useState } from "react";
import {
  SmartReviewVendor,
  useGetSmartReviewQuery,
  useUpdateMultipleTransactionsDataMutation,
} from "store/apis/transactions";
import { BackendError } from "types/utils/error";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { SmartReviewVendorGroup } from "./SmartReviewVendorGroup";
import { SmartReviewBankTransferTable } from "./SmartReviewBankTransferTable";
import { BulkCategorise } from "components/Transaction/BulkCategorise";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { GreenTickGreenCircleGradient } from "components/icons/GreenTickGreenCircleGradient";
import {
  SMART_REVIEW_TABS,
  SMART_REVIEW_TAB_LABELS,
  SmartReviewTabKey,
  SmartReviewVendorTabKey,
  SMART_REVIEW_VENDOR_TABS,
} from "constants/smartReview";
import { SmartReviewAllTabEmptyState } from "./SmartReviewAllTabEmptyState";
import { useUpdateQuery } from "hooks/useQuery";
import { TXN_GROUP_BY } from "constants/bookkeeping";
import { useDispatch } from "react-redux";
import { setTxnFilters } from "store/slices/transactionFilter";
import { setSelectedTransactionIds } from "store/slices/transactions";
import { Divider } from "components/design/Divider";
import { pluralize } from "utils/pluralize";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";

type VendorSelectionState = Record<string, string[]>;
type TabVendorSelections = Partial<
  Record<SmartReviewTabKey, VendorSelectionState>
>;

type DateParams = {
  start_date: string;
  end_date: string;
};

type SmartReviewResultsProps = {
  dateParams: DateParams;
  onClose: () => void;
};

const getVendorKey = (vendor: SmartReviewVendor) =>
  vendor.merchant_data_id ?? "unknown_merchant";

const isVendorTab = (tab: SmartReviewTabKey): tab is SmartReviewVendorTabKey =>
  (SMART_REVIEW_VENDOR_TABS as readonly string[]).includes(tab);

const EmptyVendorTabState = () => (
  <div className="t-flex t-flex-col t-items-center t-justify-center t-py-16 t-gap-3 t-h-full">
    <span className="t-flex t-items-center t-justify-center">
      <GreenTickGreenCircleGradient className="t-size-16" />
    </span>
    <p className="t-text-subtext t-text-text-100 t-mb-1 t-text-center">
      No transactions found for this period.
    </p>
  </div>
);

const EmptyBankTabState = () => (
  <div className="t-flex t-flex-col t-items-center t-justify-center t-py-16 t-gap-3 t-h-full">
    <span className="t-flex t-items-center t-justify-center">
      <GreenTickGreenCircleGradient className="t-size-16" />
    </span>
    <p className="t-text-subtext t-text-text-100 t-mb-1 t-text-center">
      All Bank transfer/paydown credit transactions in this tab have been
      confirmed.
    </p>
  </div>
);

export const SmartReviewResults = ({
  dateParams,
  onClose,
}: SmartReviewResultsProps) => {
  const [activeTab, setActiveTab] = useState<SmartReviewTabKey>(
    SMART_REVIEW_TABS.DEFAULT
  );
  const [tabVendorSelections, setTabVendorSelections] =
    useState<TabVendorSelections>({});
  const [selectedGroupNums, setSelectedGroupNums] = useState<number[]>([]);
  const [confirmingIds, setConfirmingIds] = useState<string[]>([]);
  const [updatingCategoryIds, setUpdatingCategoryIds] = useState<string[]>([]);
  const bulkCategoriseModal = useModal();

  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const dispatch = useDispatch();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );
  const { updateMultiple: updateQueryParams } = useUpdateQuery();

  const handleViewUnreviewed = () => {
    dispatch(
      setTxnFilters({
        notReviewed: "true",
        startDate: dateParams.start_date,
        endDate: dateParams.end_date,
      })
    );
    updateQueryParams([
      { query: "group_by", value: TXN_GROUP_BY.VENDOR },
      { query: "page", value: "1" },
      { query: "page_size", value: "300" },
    ]);
    onClose();
  };

  const { data } = useGetSmartReviewQuery({ groupId, entityId, ...dateParams });

  const [updateMultiple] = useUpdateMultipleTransactionsDataMutation();

  const bankTransferGroups = data?.bank_transfer?.bank_transfer_groups ?? [];

  const vendorsByTab: Record<SmartReviewVendorTabKey, SmartReviewVendor[]> = {
    [SMART_REVIEW_TABS.DEFAULT]: data?.default?.vendors ?? [],
    [SMART_REVIEW_TABS.UNMATCHED_VENDORS]:
      data?.unmatched_vendor?.vendors ?? [],
    [SMART_REVIEW_TABS.UNCATEGORISED]: data?.uncategorised?.vendors ?? [],
  };

  const activeVendors = isVendorTab(activeTab) ? vendorsByTab[activeTab] : [];
  const currentSelections = tabVendorSelections[activeTab] ?? {};

  const updateCurrentSelections = (
    updater: (prev: VendorSelectionState) => VendorSelectionState
  ) => {
    setTabVendorSelections((prev) => ({
      ...prev,
      [activeTab]: updater(prev[activeTab] ?? {}),
    }));
  };

  const currentSelectedIds = Object.values(currentSelections).flat();

  const bankTabSelectedTxnIds = bankTransferGroups
    .filter((g) => selectedGroupNums.includes(g.group_number))
    .flatMap((g) => g.transactions.map((t) => t.id));

  const bankTabSelectedTxnCount = bankTabSelectedTxnIds.length;

  const selectedCount =
    activeTab === SMART_REVIEW_TABS.BANK_TRANSFER
      ? bankTabSelectedTxnCount
      : currentSelectedIds.length;

  const bulkCategoriseIds =
    activeTab === SMART_REVIEW_TABS.BANK_TRANSFER
      ? bankTabSelectedTxnIds
      : currentSelectedIds;

  const allVendorTxnsSelected =
    activeVendors.length > 0 &&
    activeVendors.every((v) =>
      v.transactions.every((t) =>
        currentSelections[getVendorKey(v)]?.includes(t.id)
      )
    );

  const someVendorSelected =
    !allVendorTxnsSelected &&
    activeVendors.some((v) =>
      v.transactions.some((t) =>
        currentSelections[getVendorKey(v)]?.includes(t.id)
      )
    );

  const handleToggleAllVendors = () => {
    updateCurrentSelections((prev) => {
      const updated = { ...prev };
      activeVendors.forEach((v) => {
        updated[getVendorKey(v)] = allVendorTxnsSelected
          ? []
          : v.transactions.map((t) => t.id);
      });
      return updated;
    });
  };

  const handleToggleVendor = (vendorKey: string) => {
    const vendor = activeVendors.find((v) => getVendorKey(v) === vendorKey);
    if (!vendor) return;

    updateCurrentSelections((prev) => {
      const current = prev[vendorKey] ?? [];
      const allSelected = vendor.transactions.every((t) =>
        current.includes(t.id)
      );
      return {
        ...prev,
        [vendorKey]: allSelected ? [] : vendor.transactions.map((t) => t.id),
      };
    });
  };

  const handleToggleVendorTransaction = (vendorKey: string, txnId: string) => {
    updateCurrentSelections((prev) => {
      const current = prev[vendorKey] ?? [];
      const updated = current.includes(txnId)
        ? current.filter((id) => id !== txnId)
        : [...current, txnId];
      return { ...prev, [vendorKey]: updated };
    });
  };

  const handleToggleBankGroup = (groupNumber: number, checked: boolean) => {
    setSelectedGroupNums((prev) =>
      checked ? [...prev, groupNumber] : prev.filter((g) => g !== groupNumber)
    );
  };

  const handleConfirm = () => {
    let transactionsList: { uuid: string }[];
    let batchIds: string[];

    if (activeTab === SMART_REVIEW_TABS.BANK_TRANSFER) {
      transactionsList = bankTransferGroups
        .filter((g) => selectedGroupNums.includes(g.group_number))
        .flatMap((g) => g.transactions.map((t) => ({ uuid: t.id })));
      batchIds = transactionsList.map((t) => t.uuid);
      setSelectedGroupNums([]);
    } else {
      transactionsList = currentSelectedIds.map((id) => ({ uuid: id }));
      batchIds = currentSelectedIds;
      updateCurrentSelections((prev) => {
        const cleared = { ...prev };
        Object.keys(cleared).forEach((key) => {
          cleared[key] = [];
        });
        return cleared;
      });
    }

    setConfirmingIds((prev) => [...prev, ...batchIds]);

    updateMultiple({
      groupId,
      entityId,
      ai_category_confirmation: true,
      transactions: transactionsList,
    })
      .unwrap()
      .then(() => {
        successToast({ message: "Category confirmed successfully" });
      })
      .catch((error: BackendError) => {
        alertToast({ message: error?.data?.error?.message });
      })
      .finally(() => {
        setConfirmingIds((prev) => prev.filter((id) => !batchIds.includes(id)));
      });
  };

  const handleDismissSelection = () => {
    if (activeTab === SMART_REVIEW_TABS.BANK_TRANSFER) {
      setSelectedGroupNums([]);
    } else {
      updateCurrentSelections((prev) => {
        const cleared = { ...prev };
        Object.keys(cleared).forEach((key) => {
          cleared[key] = [];
        });
        return cleared;
      });
    }
  };

  const handleCategoryChange = (txnId: string, categoryId: string | null) => {
    setUpdatingCategoryIds((prev) => [...prev, txnId]);
    updateMultiple({
      groupId,
      entityId,
      transactions: [{ uuid: txnId, category_id: categoryId ?? undefined }],
    })
      .unwrap()
      .then(() => successToast({ message: "Category updated" }))
      .catch((error: BackendError) => {
        alertToast({ message: error?.data?.error?.message });
      })
      .finally(() => {
        setUpdatingCategoryIds((prev) => prev.filter((id) => id !== txnId));
      });
  };

  const tabCountData: Record<SmartReviewTabKey, number> = {
    [SMART_REVIEW_TABS.DEFAULT]: data?.default?.total_count ?? 0,
    [SMART_REVIEW_TABS.BANK_TRANSFER]:
      data?.bank_transfer?.bank_transfer_groups?.length ?? 0,
    [SMART_REVIEW_TABS.UNMATCHED_VENDORS]:
      data?.unmatched_vendor?.total_count ?? 0,
    [SMART_REVIEW_TABS.UNCATEGORISED]: data?.uncategorised?.total_count ?? 0,
  };

  const renderVendorTabContent = (tab: SmartReviewVendorTabKey) => {
    const vendors = vendorsByTab[tab];
    const selections = tabVendorSelections[tab] ?? {};

    if (vendors.length === 0) {
      if (tab === SMART_REVIEW_TABS.DEFAULT) {
        return (
          <SmartReviewAllTabEmptyState
            dateParams={dateParams}
            onClose={onClose}
            onViewUnreviewed={handleViewUnreviewed}
          />
        );
      }
      return <EmptyVendorTabState />;
    }

    const allSelected =
      vendors.length > 0 &&
      vendors.every((v) =>
        v.transactions.every((t) => selections[getVendorKey(v)]?.includes(t.id))
      );
    const someSelected =
      !allSelected &&
      vendors.some((v) =>
        v.transactions.some((t) => selections[getVendorKey(v)]?.includes(t.id))
      );

    return (
      <>
        <div className="t-flex t-items-center t-gap-3 t-py-2 t-mb-2 t-bg-surface-lighter-grey t-rounded-lg t-pl-2">
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}
            blockedUi={
              <span className="t-text-subtext-sm t-text-text-30 t-pl-1">
                Vendor name
              </span>
            }
          >
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleToggleAllVendors}
              label={
                <span className="t-text-subtext-sm t-text-text-30 t-pl-1">
                  Vendor name
                </span>
              }
            />
          </LockedUi>
        </div>
        <div className="t-pl-2">
          {vendors.map((vendor) => {
            const vendorKey = getVendorKey(vendor);
            const selectedIds = selections[vendorKey] ?? [];
            const vendorConfirmingIds = vendor.transactions
              .map((t) => t.id)
              .filter((id) => confirmingIds.includes(id));
            return (
              <SmartReviewVendorGroup
                key={vendorKey}
                vendor={vendor}
                selectedIds={selectedIds}
                confirmingIds={vendorConfirmingIds}
                updatingCategoryIds={updatingCategoryIds}
                onToggleTransaction={handleToggleVendorTransaction}
                onToggleVendor={handleToggleVendor}
                onCategoryChange={
                  hasWritePermission ? handleCategoryChange : undefined
                }
              />
            );
          })}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="t-px-8 t-py-4 sm:t-px-4">
        <p className="t-text-body-sm t-text-text-30 t-mb-0">
          We've reviewed the AI-categorised transactions and highlighted those
          with highly accurate category assignments.
          <br />
          Select transactions or vendors below to confirm the category in bulk.
        </p>
      </div>

      <Tabs.Root
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as SmartReviewTabKey)}
        className="t-flex t-flex-col t-overflow-hidden"
      >
        <div className="t-mx-8 sm:t-mx-4">
          <Tab.List>
            {Object.values(SMART_REVIEW_TABS).map((tab) => (
              <Tab.Trigger key={tab} value={tab}>
                <div className="t-flex t-gap-2 t-text-body-sm">
                  {SMART_REVIEW_TAB_LABELS[tab]}
                  <Tag
                    tagType={activeTab === tab ? "purple" : "gray"}
                    rounded
                    icon={false}
                    size="small"
                  >
                    {tabCountData[tab]}
                  </Tag>
                </div>
              </Tab.Trigger>
            ))}
          </Tab.List>
        </div>

        {SMART_REVIEW_VENDOR_TABS.map((tab) => (
          <Tabs.Content
            key={tab}
            value={tab}
            className="t-flex-1 t-overflow-auto t-px-8 t-pt-4 t-pb-28 sm:t-px-4"
          >
            {renderVendorTabContent(tab)}
          </Tabs.Content>
        ))}

        <Tabs.Content
          value={SMART_REVIEW_TABS.BANK_TRANSFER}
          className="t-flex-1 t-overflow-auto t-px-8 t-py-4 sm:t-px-4"
        >
          {bankTransferGroups.length === 0 ? (
            <EmptyBankTabState />
          ) : (
            <SmartReviewBankTransferTable
              groups={bankTransferGroups}
              selectedGroupNums={selectedGroupNums}
              confirmingIds={confirmingIds}
              updatingCategoryIds={updatingCategoryIds}
              onToggleGroup={handleToggleBankGroup}
              onCategoryChange={
                hasWritePermission ? handleCategoryChange : undefined
              }
            />
          )}
        </Tabs.Content>
      </Tabs.Root>

      <SelectedFloatingControl>
        {selectedCount > 0 ? (
          <>
            <span className="t-whitespace-nowrap t-text-body-sm t-text-text-60">
              <span className="sm:t-hidden">
                {pluralize(selectedCount, "transaction", "transactions")}{" "}
                selected
              </span>
              <span className="t-hidden sm:t-inline">
                {selectedCount} selected
              </span>
            </span>
            <Divider orientation="vertical" classNames="t-h-5" />
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}
              blockedUi={
                <>
                  <LockedButton text="Categorise" />
                  <LockedButton text="Confirm category" />
                </>
              }
            >
              <Button
                customType="secondary"
                size="small"
                onClick={() => {
                  dispatch(setSelectedTransactionIds(bulkCategoriseIds));
                  bulkCategoriseModal.open();
                }}
              >
                Categorise
              </Button>
              <Button
                customType="secondary"
                size="small"
                onClick={handleConfirm}
              >
                Confirm<span className="sm:t-hidden">&nbsp;category</span>
              </Button>
            </LockedUi>
            <Button
              customType="ghost_icon"
              size="small"
              onClick={handleDismissSelection}
            >
              <Cross />
            </Button>
          </>
        ) : null}
      </SelectedFloatingControl>

      {bulkCategoriseModal.isOpen && (
        <BulkCategorise
          isOpen={bulkCategoriseModal.isOpen}
          close={bulkCategoriseModal.close}
          deselectRows={handleDismissSelection}
          unSelectedRowsIds={[]}
          isAllTxnSelected={false}
        />
      )}
    </>
  );
};
